import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import styled, { keyframes, css } from 'styled-components';
import PropTypes from 'prop-types';
import DialogHeader from './DialogHeader';
import Container from './Container';
import Text from './Text';
import Icon from './Icon';
import EmptyButton from './EmptyButton';
import ClickOutside from './ClickOutside';

const slideIn = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const fadeAnimation = ({ show, animationDuration }) =>
  css`
    ${show ? fadeIn : fadeOut} ${animationDuration}ms
  `;

const slideAnimation = ({ show, animationDuration }) =>
  css`
    ${show ? slideIn : slideOut} ${animationDuration}ms
  `;

const FadeContainer = styled(Container)`
  display: flex;
  opacity: ${props => (props.show ? 1 : 0)};
  animation: ${fadeAnimation};
`;

const SlideContainer = styled(Container)`
  display: flex;
  transform: translateY(${props => (props.show ? 0 : '100%')};);
  animation: ${slideAnimation};
`;

const defaultRender = data => {
  const { header, children, onClose } = data;
  return (
    <React.Fragment>
      {typeof header === 'string' ? (
        <DialogHeader>
          <Text
            width="120px"
            flex="1 0 auto"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {header}
          </Text>
          <EmptyButton
            onClick={onClose}
            display="flex"
            alignItems="center"
            overflow="hidden"
          >
            <Icon iconSize="xxlarge" className="ls-icon icon-generalxsmall" />
          </EmptyButton>
        </DialogHeader>
      ) : (
        header
      )}
      <Container flex="0 1 100%">{children}</Container>
    </React.Fragment>
  );
};

const Dialog = ({
  show,
  render,
  children,
  header,
  onClose,
  mode,
  animationDuration,
  clickOutside,
  zIndex,
  ...props
}) => {
  const [inDom, setInDom] = useState(false);
  const [containerRef, setContainerRef] = useState();

  useEffect(() => {
    if (show) {
      setInDom(true);
    } else {
      setTimeout(() => {
        setInDom(false);
      }, animationDuration);
    }
  }, [show, animationDuration, containerRef]);

  useEffect(() => {
    if (inDom && containerRef) {
      disableBodyScroll(containerRef);
    }
    if (!inDom && containerRef) {
      enableBodyScroll(containerRef);
    }
  }, [containerRef, inDom]);

  const dialogStyle = useMemo(
    () =>
      mode !== 'desktop'
        ? {
            position: 'fixed',
            top: '0px',
            left: '0px',
            right: '0px',
            bottom: '0px',
            borderRadius: '0',
            maxWidth: '100vw',
          }
        : {},
    [mode]
  );

  if (!inDom) return null;

  return createPortal(
    <FadeContainer
      position="fixed"
      zIndex={zIndex}
      top="0"
      left="0"
      right="0"
      bottom="0"
      display="flex"
      alignItems="center"
      justifyContent="center"
      backgroundColor="rgba(0, 0, 0, 0.4)"
      padding="15px"
      show={show}
      animationDuration={animationDuration}
    >
      <ClickOutside onClickOutside={clickOutside ? onClose : () => {}}>
        <SlideContainer
          ref={ref => {
            setContainerRef(ref);
          }}
          display="flex"
          flexDirection="column"
          border="solid 1px"
          borderColor="border300"
          backgroundColor="background100"
          boxShadow="0 2px 4px 0 rgba(0, 0, 0, 0.5)"
          width="100vw"
          maxHeight="100vh"
          show={show}
          animationDuration={animationDuration}
          overflow="auto"
          borderRadius="11px"
          {...props}
          {...dialogStyle}
        >
          {render({
            header,
            children,
            onClose,
          })}
        </SlideContainer>
      </ClickOutside>
    </FadeContainer>,
    document.body
  );
};

Dialog.propTypes = {
  show: PropTypes.bool,
  render: PropTypes.func,
  children: PropTypes.node,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onClose: PropTypes.func,
  mode: PropTypes.string,
  maxWidth: PropTypes.string,
  animationDuration: PropTypes.number,
  clickOutside: PropTypes.bool,
  zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Dialog.defaultProps = {
  show: false,
  render: defaultRender,
  children: null,
  header: null,
  onClose: () => {},
  mode: 'desktop',
  maxWidth: '600px',
  animationDuration: 300,
  clickOutside: false,
  zIndex: '99',
};

export default Dialog;
